import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { it, enUS } from 'date-fns/locale';
import { Skeleton } from '../../ui/skeleton';
import { ToggleGroup, ToggleGroupItem } from '../../ui/toggle-group';
import { insightsService } from '../../../services/insights';
import ClimberIndexCard from './ClimberIndexCard';
import InsightsSummaryRow from './InsightsSummaryRow';
import PercentileCurve from './PercentileCurve';
import RareSendsList from './RareSendsList';
import type { InsightClimbType, UserInsight } from '../../../types/insight';

interface UserInsightsProps {
  userSlug: string;
}

export default function UserInsights({ userSlug }: UserInsightsProps) {
  const { t, i18n } = useTranslation();
  const [climbType, setClimbType] = useState<InsightClimbType>('Boulder');
  const [insight, setInsight] = useState<UserInsight | null>(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    // Toggling Boulder/Route starts a second request while the first is still
    // in flight, and the responses can land out of order — so anything that
    // arrives after the inputs changed is dropped rather than rendered.
    let current = true;

    const load = async () => {
      setLoading(true);
      setFailed(false);
      try {
        const result = await insightsService.getUserInsights(userSlug, climbType);
        if (current) setInsight(result);
      } catch (error) {
        console.error('Failed to load insights:', error);
        if (current) setFailed(true);
      } finally {
        if (current) setLoading(false);
      }
    };

    load();

    return () => {
      current = false;
    };
  }, [userSlug, climbType]);

  if (loading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-20 w-full" />
        <Skeleton className="h-40 w-full" />
      </div>
    );
  }

  if (failed || !insight) {
    return (
      <p className="py-8 text-center text-sm text-muted-foreground">
        {t('userProfile.insights.loadError')}
      </p>
    );
  }

  // Offering a climb type this climber has nothing for would only produce an
  // empty tab, so the toggle appears exactly when there is a choice to make.
  const showToggle = insight.available_climb_types.length > 1;

  return (
    <div className="space-y-4">
      {showToggle && (
        <ToggleGroup
          type="single"
          value={climbType}
          onValueChange={(value) => {
            if (value) setClimbType(value as InsightClimbType);
          }}
          className="justify-start"
        >
          {insight.available_climb_types.map((type) => (
            <ToggleGroupItem key={type} value={type} className="px-4">
              {t(`stats.climbType.${type.toLowerCase()}`)}
            </ToggleGroupItem>
          ))}
        </ToggleGroup>
      )}

      <ClimberIndexCard insight={insight} />

      {insight.scored && <InsightsSummaryRow insight={insight} />}

      {/* The curve only means something to somebody who is in the cohort it
          draws — without a marker it is just a shape. */}
      {insight.ranked && <PercentileCurve insight={insight} />}

      <RareSendsList sends={insight.rare_sends} />

      {insight.computed_at && (
        <p className="text-center text-xs text-muted-foreground">
          {t('userProfile.insights.updatedAt', {
            date: format(new Date(insight.computed_at), 'd MMM yyyy, HH:mm', {
              locale: i18n.language === 'it' ? it : enUS,
            }),
          })}
        </p>
      )}
    </div>
  );
}
